import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../../api/client';

function AdminAssignPublishModal({ show, onHide, visit, doctors = [], serviceTypes = [], adminUserId, onSuccess }) {
  const [assignDoctorId, setAssignDoctorId] = useState('');
  const [doctorRate, setDoctorRate] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const canPublish = visit?.canPublish === true;
  const canAssign = visit?.canAssign === true;
  const isAlreadyPublished = visit?.status === 'published';

  const resolveDefaultDoctorRate = () => {
    if (!visit) return '';
    const serviceType = serviceTypes.find(
      (s) => String(s.idServiceType) === String(visit.serviceTypeId),
    );
    const defaultFromService = serviceType?.defaultRateDoctor;
    const isDoctorRateLocked = ['published', 'assigned', 'completed', 'paid'].includes(visit.status);
    if (isDoctorRateLocked && visit.agreedRate > 0) {
      return String(visit.agreedRate);
    }
    if (defaultFromService != null && Number(defaultFromService) > 0) {
      return String(defaultFromService);
    }
    return '';
  };

  useEffect(() => {
    if (show) {
      setAssignDoctorId('');
      setDoctorRate(resolveDefaultDoctorRate());
      setIsAssigning(false);
      setIsPublishing(false);
    }
  }, [show, visit?.id, visit?.serviceTypeId, visit?.status, visit?.agreedRate, serviceTypes]);

  const handleClose = () => {
    setAssignDoctorId('');
    setDoctorRate('');
    onHide();
  };

  const validateDoctorRate = () => {
    const rate = parseFloat(doctorRate);
    if (!doctorRate || Number.isNaN(rate) || rate <= 0) {
      toast.error('Please enter a valid doctor rate greater than zero.');
      return null;
    }
    return rate;
  };

  const handleAssignToDoctor = async () => {
    if (!visit) return;
    if (!assignDoctorId) {
      toast.error('Please select a doctor.');
      return;
    }
    const rate = validateDoctorRate();
    if (rate == null) return;

    setIsAssigning(true);
    try {
      const res = await client.post('/api/admin/visit-requests/assign', {
        idVisitRequest: visit.id,
        assignedDoctorID: parseInt(assignDoctorId, 10),
        adminUserID: adminUserId,
        agreedRate: rate,
      });
      if (res.data?.success) {
        toast.success('Doctor assigned successfully.');
        handleClose();
        onSuccess?.();
      } else {
        toast.error(res.data?.message || 'Assign failed.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Assign failed.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handlePublishToAll = async () => {
    if (!visit) return;
    const rate = validateDoctorRate();
    if (rate == null) return;

    setIsPublishing(true);
    try {
      const res = await client.post('/api/admin/visit-requests/publish', {
        idVisitRequest: visit.id,
        adminUserID: adminUserId,
        agreedRate: rate,
      });
      if (res.data?.success) {
        toast.success('Published to all doctors.');
        handleClose();
        onSuccess?.();
      } else {
        toast.error(res.data?.message || 'Publish failed.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Publish failed.');
    } finally {
      setIsPublishing(false);
    }
  };

  if (!visit) return null;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Assign / Publish Visit Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-2 mb-3 p-3 rounded bg-light border visit-assign-summary">
          <div className="col-6 col-md-3">
            <small className="text-muted d-block">Request</small>
            <strong>{visit.bookingNumber}</strong>
          </div>
          <div className="col-6 col-md-3">
            <small className="text-muted d-block">Patient</small>
            <strong>{visit.patientName}</strong>
          </div>
          <div className="col-6 col-md-3">
            <small className="text-muted d-block">Phone Number</small>
            <strong>
              {visit.patientMobile ? (
                <a href={`tel:${visit.patientMobile}`} className="text-decoration-none">
                  {visit.patientMobile}
                </a>
              ) : (
                '—'
              )}
            </strong>
          </div>
          <div className="col-6 col-md-3">
            <small className="text-muted d-block">Date</small>
            <strong>
              {visit.preferredDate} {visit.preferredTime}
            </strong>
          </div>
          <div className="col-12">
            <small className="text-muted d-block">Email ID</small>
            <strong className="visit-assign-email text-break">
              {visit.patientEmail ? (
                <a href={`mailto:${visit.patientEmail}`} className="text-decoration-none text-break">
                  {visit.patientEmail}
                </a>
              ) : (
                '—'
              )}
            </strong>
          </div>
          <div className="col-12">
            <small className="text-muted d-block">Address</small>
            <strong className="text-break">{visit.patientAddress || '—'}</strong>
            {visit.patientLandmark && (
              <div className="small text-muted mt-1 text-break">Landmark: {visit.patientLandmark}</div>
            )}
          </div>
          <div className="col-12">
            <small className="text-muted d-block">Service</small>
            <strong>{visit.serviceTypeName}</strong>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="doctorRate" className="form-label fw-semibold">
            Doctor Rate (₹) <span className="text-danger">*</span>
          </label>
          <input
            id="doctorRate"
            type="number"
            className="form-control"
            min="0.01"
            step="0.01"
            value={doctorRate}
            onChange={(e) => setDoctorRate(e.target.value)}
            placeholder="Default from service type"
          />
          <div className="form-text">
            Pre-filled from the service type default doctor rate. You can override before publish or assign.
            This amount is saved as the agreed rate and shown to doctors.
          </div>
        </div>

        <p className="text-muted small mb-3">
          Choose to assign a specific doctor directly, or publish the request so all approved
          doctors can view and accept it.
        </p>

        <div className="d-flex flex-column gap-3">
          {canAssign && (
            <div className="visit-assign-option visit-assign-option-primary border border-primary border-2 rounded p-3">
              <div className="fw-bold text-primary mb-2">Option 1 – Assign Specific Doctor</div>
              <label className="form-label mb-1">
                Select Doctor <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={assignDoctorId}
                onChange={(e) => setAssignDoctorId(e.target.value)}
              >
                <option value="">Choose a doctor...</option>
                {doctors.map((d) => (
                  <option key={d.professionalID} value={d.professionalID}>
                    {d.professionalName}
                    {d.specialization ? ` — ${d.specialization}` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-3"
                onClick={handleAssignToDoctor}
                disabled={isAssigning || isPublishing}
              >
                {isAssigning ? 'Assigning...' : 'Assign to Doctor'}
              </button>
            </div>
          )}

          {canPublish && (
            <div className="visit-assign-option border rounded p-3">
              <div className="fw-bold mb-2" style={{ color: '#124189' }}>
                Option 2 – Publish to All Doctors
              </div>
              <p className="text-muted small mb-2">
                The request will be visible to all approved doctors. First to accept gets the
                job.
              </p>
              <button
                type="button"
                className="btn btn-warning btn-sm"
                onClick={handlePublishToAll}
                disabled={isAssigning || isPublishing}
              >
                {isPublishing ? 'Publishing...' : 'Publish to All Doctors'}
              </button>
            </div>
          )}

          {isAlreadyPublished && !canPublish && canAssign && (
            <p className="text-muted small mb-0">
              This request is already published. Assign a specific doctor below.
            </p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={handleClose}>
          Cancel
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default AdminAssignPublishModal;
